/* eslint-disable complexity */
import { jsPDF } from 'jspdf';
import { RefObject } from 'react';
import { IntlShape } from 'react-intl';
import * as THREE from 'three';
import { exportToPdfProps, IBoundingBox, IImageExportData, IImgData } from '../@types/Components';
import { IBuildingProviderContext, IFileInfo } from '../@types/ContextProviderRelated';
import { EOrientation, EPageOrientation, EPageOrientationInputParameter, EUnitType, EWall } from '../@types/Enums';
import { font as FontRoboto } from '../export/fontscripts/Roboto-Regular-normal';
import { PdfCursor } from '../export/PdfCursor';
import { getValidKeycloakAccessTokenOrFallback } from '../keycloak';
import { IDoor } from '../models/standard/DoorModel';
import { IStandardWall } from '../models/standard/StandardWallModel';
import {
  CAMERA_FAR_DISTANCE,
  CAMERA_NEAR_DISTANCE,
  CAMERA_Z_INDEX,
  CANVAS_PADDING_FACTOR,
  CM_TO_INCH_MULTIPLIER,
  COMPASS_BACKGROUND_COLOR,
  COMPASS_BACKGROUND_OPACITY,
  COMPASS_FONT_SIZE,
  COMPASS_PADDING,
  COMPASS_SIZE,
  COMPASS_SPACING_TO_TEXT,
  CREATE_SCENE_IMAGE_SLEEP_TIME,
  DEFAULT_AUTO_REFERENCE_SLICE,
  DEFAULT_CANVAS_MAX_SIZE_MULTIPLIER,
  DRAW_IMAGE_BORDER,
  FONT_TO_MM,
  HALF_CIRCLE_DEGREES,
  HEADER_FONT_SIZE,
  HEADER_FONT_SPACING_BOTTOM,
  HEADER_PADDING_X,
  HEADER_SPACING_BOTTOM,
  HEADER_SPACING_TOP,
  HORIZONTAL_DIVIDER_LINE_THICKNESS,
  IFC_URL,
  IMAGE_SPACING_BOTTOM,
  IMG_COMPASS_FONT_CSS_SUFFIX,
  IMG_COMPASS_FONT_SIZE,
  IMG_COMPASS_PADDING,
  IMG_COMPASS_SIZE,
  IMG_COMPASS_SPACING_TO_TEXT,
  IMG_SCALE_COLOR,
  IMG_SCALE_FONT,
  IMG_SCALE_REF_FONTSIZE,
  IMG_SCALE_REF_LINE_THICKNESS,
  IMG_SCALE_REF_PADDING_BOTTOM,
  IMG_SCALE_REF_PADDING_RIGHT,
  IMG_SCALE_REF_SIZE,
  MASTER_PAGE_PADDING,
  PAGE_NUMBER_FONT_SIZE,
  PDF_CURSOR_HEIGHT_OFFSET_MULTIPLIER,
  PDF_SCALE_LABEL_FONTSIZE,
  PDF_SCALE_LABEL_GAP,
  PDF_SCALE_LINE_THICKNESS,
  PDF_SCALE_PADDING_RIGHT,
  PDF_SCALE_UP_TRANSLATE,
  PREVENT_DIVISION_BY_ZERO,
  RGB_8BIT_MAX_VALUE,
  RING_GEOMETRY_CENTER,
  TEXT_BLOCK_PADDING_Y,
  TEXT_FONT_SIZE,
  TEXT_PADDING_X,
  TITLE_FONT_SIZE,
  TITLE_SPACING_BOTTOM,
  USE_FULL_SIZE_PERCENTAGE,
} from './constants';
import { formatDateIntl, formatTime } from './dateUtils';

/*
    Create a renderer here, so there is only one renderer for the exports.
    This prevents having too many renderers (would delete the oldest renderer, which is the viewport).
*/
const renderer = new THREE.WebGLRenderer({
  preserveDrawingBuffer: true,
  antialias: true,
  alpha: true,
  premultipliedAlpha: false,
  powerPreference: 'high-performance', // Hint for PCs to use discrete GPU
});

// dialed back to 6K (6144px). 8K (8192px) is often too unstable for browser contexts on Windows.
const SOFT_EXPORT_MAX_PX = 6144;
const HARD_LIMIT_SAFETY_MARGIN = 0.9;
const MAX_AREA_PX = 40_000_000; // ~40MP Area Cap (Safe 6k x 6k)

const getWebGLHardLimitPx = (): number => {
  const gl = renderer.getContext();
  if (gl.isContextLost()) return 4096;
  const maxTex = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
  const maxRb = gl.getParameter(gl.MAX_RENDERBUFFER_SIZE) as number;
  return Math.min(maxTex, maxRb);
};

// Calculates safe width/height maintaining aspect ratio
function getSafeDimensions(w: number, h: number) {
  const hard = getWebGLHardLimitPx();
  const safeMax = Math.min(SOFT_EXPORT_MAX_PX, Math.floor(hard * HARD_LIMIT_SAFETY_MARGIN));

  let targetW = w;
  let targetH = h;

  // clamp dimension
  if (targetW > safeMax || targetH > safeMax) {
    const s = safeMax / Math.max(targetW, targetH);
    targetW = targetW * s;
    targetH = targetH * s;
  }

  // clamp Area (VRAM protection)
  if (targetW * targetH > MAX_AREA_PX) {
    const s = Math.sqrt(MAX_AREA_PX / (targetW * targetH));
    targetW = targetW * s;
    targetH = targetH * s;
  }

  // always return integers (floats can cause driver clamping / mismatch issues)
  return {
    width: Math.max(1, Math.floor(targetW)),
    height: Math.max(1, Math.floor(targetH)),
  };
}

/**
 * Get the dimensions of the building
 * @param {IBuildingProviderContext} building reference to the building (from the building provider)
 * @returns all relevant information about the buildings measurements
 */
export const getBoundingBox = (building: IBuildingProviderContext): IBoundingBox => {
  const nullResult = {
    minX: 0,
    maxX: 0,
    minY: 0,
    maxY: 0,
    width: 0,
    height: 0,
    center: [0, 0],
    maxDimensionName: '',
    maxDimensionSize: 0,
    rect: [0, 0],
  };
  if (building === null || building.walls === null || building.walls.length === 0) return nullResult;

  const pointsX = [];
  const pointsY = [];

  // Let closed = true;

  for (let i = 0; i < building.walls.length; i++) {
    const raw = building.walls[i];
    if (raw.type !== EWall.STANDARD) continue;
    const wall = raw as IStandardWall;
    // Iterate through doors related to the specific wall and check for a bounding offset
    let doorOffset = 0;
    const wallRelatedDoors: Array<IDoor> = wall.doors;
    wallRelatedDoors.forEach((door) => {
      if (door.orientation === EOrientation.OUTWARD) {
        doorOffset = Math.max(doorOffset, door.length);
      }
    });

    // Add wall point for bounding rect calculation. Add door offset if it exists.
    try {
      let { x: x1, y: y1 } = wall.outerStartPoint;
      let { x: x2, y: y2 } = wall.outerEndPoint;

      if (doorOffset > 0) {
        // Calculate direction vector
        const dx = x2 - x1;
        const dy = y2 - y1;

        // Normalize direction vector
        const len = Math.hypot(dx, dy);
        const nx = -dy / len;
        const ny = dx / len;

        // Move points by the specific offset
        x1 += nx * doorOffset;
        y1 += ny * doorOffset;
        x2 += nx * doorOffset;
        y2 += ny * doorOffset;
      }
      pointsX.push(x1, x2);
      pointsY.push(y1, y2);
    } catch (e) {
      break;
    }
  }

  // Closed will be false if building contains partition walls // TODO: differentiate between types of walls
  // If (closed === false) return nullResult;

  // Get the bounds of the rect
  const minX = Math.min(...pointsX);
  const maxX = Math.max(...pointsX);
  const minY = Math.min(...pointsY);
  const maxY = Math.max(...pointsY);

  // Calculate other values of the bounding rect
  const width = maxX - minX;
  const height = maxY - minY;
  const centerX = minX + width / 2;
  const centerY = minY + height / 2;

  const maxDimensionName = width > height ? 'width' : 'height';
  const maxDimensionSize = width > height ? width : height;

  return {
    minX: minX,
    maxX: maxX,
    minY: minY,
    maxY: maxY,
    width: width,
    height: height,
    center: [centerX, centerY],
    maxDimensionName: maxDimensionName,
    maxDimensionSize: maxDimensionSize,
    rect: [0, 0],
  };
};

/**
 * Auxilliary function to sleep a certain amount of time.
 * @param milliseconds amount of time that is required to timeout.
 * @returns Promise<number> exit code of the timeout.
 */
export const sleep = (milliseconds: number): Promise<number> => new Promise((resolve) => setTimeout(resolve, milliseconds));

/**
 * Hide/Show unwanted objects for the image. Call before and after the scene image gets rendered.
 * @param {THREE.Scene} scene Reference to the scene.
 * @param {IBoundingBox} bounds Bounding box of the building.
 * @param {boolean} hide Set true to hide; Set false to un-hide.
 */
const toggleSceneClean = (scene: RefObject<THREE.Scene>, bounds: IBoundingBox, hide: boolean): void => {
  const showCenterRing = false; // Enable for debug, shows the center of the buildings bounding box

  if (hide) {
    if (showCenterRing) {
      // Only debug
      const ringGeo = RING_GEOMETRY_CENTER;
      const ringMat = new THREE.LineBasicMaterial({
        color: new THREE.Color(RGB_8BIT_MAX_VALUE, 0, 0),
      });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.position.set(bounds.center[0], bounds.center[1], 0);
      ring.name = 'center_ring';
      scene.current!.add(ring);
    }
    // Placeholder for objects that should be hidden.
  } else {
    if (showCenterRing) {
      // Only debug
      const ring = scene.current!.getObjectByName('center_ring');
      if (ring !== undefined) scene.current!.remove(ring);
    }
    // Placeholder for objects that should be unhidden.
  }

  // Go through all objects of the scene and hide the grid and axes.
  const hideHelpers = (): void => {
    if (scene.current !== null) {
      if (scene.current!.children.length !== 0) {
        for (const child of scene.current!.children) {
          if (
            // -- FILTER for everything that should be hidden --
            child.name.includes('gridHelper') ||
            child.name.includes('axes-helper')
            // -- FILTER END --
          ) {
            child.visible = hide === false;
          }
        }
      }
    }
  };

  hideHelpers();
};

/**
 * Creates an image of the current scene with the building in center.
 * @param {THREE.Scene} scene Reference to the scene.
 * @param {IBoundingBox} bounds The boundingBox object of the building, return value from getBoundingBox().
 * @param {function} resetSelection Function that resets all selection.
 * @param {number} imgWidth The width the resulting image should be.
 * @param {number} imgHeight The height the resulting image should be.
 * @param {number} upres Multiplier for the width and height.
 * @param {string} fileTypeExtension string file type extension. (e.g. "png", "jpeg", "bmp", "svg", etc.)
 * @param {boolean} transparent flag to indicate if the scene should get rendered transparent or with a background.
 * @param {boolean} _showGeneralInfo flag to indicate if the scale widget / compass should also get exported.
 * @returns Object of the canvas element with the image.
 */
export const createSceneImage = async (
  scene: RefObject<THREE.Scene>,
  bounds: IBoundingBox,
  resetSelection: () => void,
  imgWidth: number,
  imgHeight: number,
  upres: number,
  fileTypeExtension: string,
  transparent: boolean,
  _showGeneralInfo: boolean,
): Promise<IImgData> => {
  resetSelection();

  // Let selection/UI settle
  await sleep(CREATE_SCENE_IMAGE_SLEEP_TIME);

  // clamp requested dimensions ---
  const requestedW = imgWidth * upres;
  const requestedH = imgHeight * upres;
  const safeDims = getSafeDimensions(requestedW, requestedH);

  // we will treat these as the "target", but may overwrite them with driver-clamped actuals
  let renderW = safeDims.width;
  let renderH = safeDims.height;

  // safe guard
  if (!scene.current) {
    const emptyCanvas = document.createElement('canvas');
    emptyCanvas.width = 1;
    emptyCanvas.height = 1;
    return {
      imgData: '',
      imgCanvas: emptyCanvas,
      scaleLengthRelative: 0,
      scaleMeasurementCm: 0,
      scaleLengthRelativeImperial: 0,
      scaleMeasurementIn: 0,
    };
  }

  // calculate camera size based on target export size (may be updated after clamp)
  let { camHeight, camWidth } = calculateCameraDimensions(bounds, renderW, renderH);

  // Camera rect
  let camRect = {
    left: -camWidth / 2,
    right: camWidth / 2,
    top: camHeight / 2,
    bottom: -camHeight / 2,
  };

  // clean the scene (hide helpers etc.)
  toggleSceneClean(scene, bounds, true);

  // create export camera
  const camera = new THREE.OrthographicCamera(camRect.left, camRect.right, camRect.top, camRect.bottom, CAMERA_NEAR_DISTANCE, CAMERA_FAR_DISTANCE);
  camera.position.set(bounds.center[0], bounds.center[1], CAMERA_Z_INDEX);
  camera.updateProjectionMatrix();

  // remember scene background
  const rememberedBackground = scene.current.background;

  // apply background behavior
  // if transparent export: remove scene background so alpha can come through
  if (transparent) {
    scene.current.background = null;
  }

  // save renderer state to restore later
  const prevPR = renderer.getPixelRatio();
  const prevSize = new THREE.Vector2();
  renderer.getSize(prevSize);

  const prevClearColor = new THREE.Color();
  renderer.getClearColor(prevClearColor);
  const prevClearAlpha = renderer.getClearAlpha();

  try {
    // --- Export render setup ---
    renderer.setPixelRatio(1);
    renderer.setSize(renderW, renderH, false);

    const gl = renderer.getContext();

    // if driver clamps the drawing buffer, use ACTUAL size as truth
    const actualW = gl.drawingBufferWidth;
    const actualH = gl.drawingBufferHeight;

    if (actualW !== renderW || actualH !== renderH) {
      console.warn('drawingBuffer clamped by driver:', {
        requested: [renderW, renderH],
        actual: [actualW, actualH],
      });

      // make renderer/canvas match actual buffer
      renderer.setSize(actualW, actualH, false);

      renderW = actualW;
      renderH = actualH;

      // recompute camera for the new actual aspect/size
      ({ camHeight, camWidth } = calculateCameraDimensions(bounds, renderW, renderH));
      camRect = {
        left: -camWidth / 2,
        right: camWidth / 2,
        top: camHeight / 2,
        bottom: -camHeight / 2,
      };

      camera.left = camRect.left;
      camera.right = camRect.right;
      camera.top = camRect.top;
      camera.bottom = camRect.bottom;
      camera.updateProjectionMatrix();
    }

    // reset GL state
    renderer.resetState();
    renderer.setScissorTest(false);
    renderer.setViewport(0, 0, renderW, renderH);
    renderer.setScissor(0, 0, renderW, renderH);

    // if transparent export: clear to WHITE with alpha 0 (works correctly when premultipliedAlpha:false)
    // if non-transparent: clear to WHITE alpha 1
    if (transparent) {
      renderer.setClearColor(0xffffff, 0);
    } else {
      renderer.setClearColor(0xffffff, 1);
    }

    renderer.clear(true, true, true);

    // warmup / compile (helps some Windows/Chrome paths) ---
    renderer.compile(scene.current, camera);
    renderer.render(scene.current, camera);
    if (typeof (gl as any).finish === 'function') (gl as any).finish();

    // Yield a bit (race-condition mitigation on some Windows drivers)
    await sleep(100);

    // reassert state before final render (some drivers “forget” it apparently)
    renderer.resetState();
    renderer.setScissorTest(false);
    renderer.setViewport(0, 0, renderW, renderH);
    renderer.setScissor(0, 0, renderW, renderH);

    // clear again to avoid leftover junk in the buffer
    renderer.clear(true, true, true);

    // final render
    renderer.render(scene.current, camera);
    if (typeof (gl as any).finish === 'function') (gl as any).finish();

    // copy into result canvas
    const resultCanvas = document.createElement('canvas');
    resultCanvas.width = renderW;
    resultCanvas.height = renderH;

    const ctx = resultCanvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(renderer.domElement, 0, 0, renderW, renderH);
    }

    // undo scene edits
    toggleSceneClean(scene, bounds, false);

    // restore scene background
    scene.current.background = rememberedBackground;

    // restore renderer state
    renderer.setClearColor(prevClearColor, prevClearAlpha);
    renderer.setPixelRatio(prevPR);
    renderer.setSize(prevSize.x, prevSize.y, false);

    // return scale data (use final camWidth after clamp adjustments)
    const { lengthRelative, measurementCm, lengthRelativeImperial, measurementIn } = getScaleData(camWidth, renderW);

    return {
      imgData: '',
      imgCanvas: resultCanvas,
      scaleLengthRelative: lengthRelative,
      scaleMeasurementCm: measurementCm,
      scaleLengthRelativeImperial: lengthRelativeImperial,
      scaleMeasurementIn: measurementIn,
    };
  } catch (err) {
    // best-effort restore on failure
    try {
      toggleSceneClean(scene, bounds, false);
      if (scene.current) scene.current.background = rememberedBackground;
      renderer.setClearColor(prevClearColor, prevClearAlpha);
      renderer.setPixelRatio(prevPR);
      renderer.setSize(prevSize.x, prevSize.y, false);
    } catch (_) {
      // ignore secondary restore errors
    }

    console.error('createSceneImage failed:', err);

    const fallbackCanvas = document.createElement('canvas');
    fallbackCanvas.width = 1;
    fallbackCanvas.height = 1;

    return {
      imgData: '',
      imgCanvas: fallbackCanvas,
      scaleLengthRelative: 0,
      scaleMeasurementCm: 0,
      scaleLengthRelativeImperial: 0,
      scaleMeasurementIn: 0,
    };
  }
};

/**
 * Function to calculate the dimensions of the camera used for exporting.
 *
 * @author Daniel Rösch
 * @date 03.10.2024
 * @param {IBoundingBox} bounds the bounding box of the building in the scene to get exported.
 * @param {number} imgWidth the width of the image in pixels.
 * @param {number} imgHeight the height of the image in pixels.
 * @returns {{ camHeight: number; camWidth: number }} object containing the camera height (equal to the distance to the canvas) and the width of the camera's intersection plane with the canvas.
 */
export const calculateCameraDimensions = (bounds: IBoundingBox, imgWidth: number, imgHeight: number): { camHeight: number; camWidth: number } => {
  // Calculate the aspect ratio of the image
  const aspect = imgWidth / imgHeight;

  // Canvas padding to ensure objects near the edge are included
  const canvasPadding = bounds.maxDimensionSize * CANVAS_PADDING_FACTOR;

  // Expand the bounding box dimensions by padding
  const paddedBounds = {
    width: bounds.width + canvasPadding * 2,
    height: bounds.height + canvasPadding * 2,
  };

  // Initialize camera height and width
  let camHeight = 0;
  let camWidth = 0;

  // Case 1: If the padded width is larger, determine camera size based on width
  if (paddedBounds.width >= paddedBounds.height) {
    camWidth = paddedBounds.width;
    camHeight = camWidth / aspect;
    if (paddedBounds.height > camHeight) {
      camHeight = paddedBounds.height;
      camWidth = camHeight * aspect;
    }
  }
  // Case 2: If the padded height is larger, determine camera size based on height
  else {
    camHeight = paddedBounds.height;
    camWidth = camHeight * aspect;
    if (paddedBounds.width > camWidth) {
      camWidth = paddedBounds.width;
      camHeight = camWidth / aspect;
    }
  }

  return { camHeight, camWidth };
};

/**
 * Pre-calculates the camera dimensions (height and width) based on the building dimensions, scene, and pixel per decimeter.
 * Updates the camera height and width in the export context if applicable.
 *
 * @param {IBuildingProviderContext} building The building object used to get the bounding box.
 * @param {RefObject<THREE.Scene>} scene Reference to the current Three.js scene.
 * @param {number} pixelPerDecimeter The resolution scaling factor in pixels per decimeter.
 * @param {(dimensions: { camHeight: number, camWidth: number }) => void} setExportCameraDimensions Function to set the calculated camera dimensions in the export context.
 * @param {() => void} setIsExporting Optional function to set the exporting flag if needed.
 */
export const preCalculateCameraDimensions = (
  building: IBuildingProviderContext,
  scene: RefObject<THREE.Scene>,
  pixelPerDecimeter: number,
  setExportCameraDimensions: (dimensions: { camHeight: number; camWidth: number }) => void,
  setIsExporting?: () => void,
) => {
  if (building && scene?.current) {
    const bounds = getBoundingBox(building);

    // Dynamically determine image width and height based on the export logic
    let imageWidth = bounds.width * pixelPerDecimeter;
    let imageHeight = bounds.height * pixelPerDecimeter;
    const aspectRatio = imageWidth / imageHeight;

    // Adjust dimensions dynamically based on the aspect ratio (if needed)
    if (aspectRatio > 1) {
      imageHeight = imageWidth / aspectRatio;
    } else {
      imageWidth = imageHeight * aspectRatio;
    }

    // Calculate camera height and width based on dimensions
    const { camHeight, camWidth } = calculateCameraDimensions(bounds, imageWidth, imageHeight);

    // Set the calculated camera height and width in the export context
    setExportCameraDimensions({ camHeight, camWidth });

    // Optionally set the exporting flag
    if (setIsExporting) {
      setIsExporting();
    }
  }
};

/**
 * Function to get the internal data and scale it to represent different units.
 *
 * @param {number} camWidthU the numerical width of the intersection plane of the camera and the floorplanner main plane, expressed in internal units (which are equivalent to decimeters.).
 * @param {number} imgWidthPx the numerical width of the intersection plane of the camera and the floorplanner main plane, expressed in pixels.
 * @returns {{lengthRelative: number, measurementCm: number, lengthRelativeImperial: number, measurementIn: number}} object containing the relative length of the scaled image and the measurement in centimeters, as well as their imperial equivalents.
 */
const getScaleData = (
  camWidthU: number,
  imgWidthPx: number,
): {
  lengthRelative: number;
  measurementCm: number;
  lengthRelativeImperial: number;
  measurementIn: number;
} => {
  const imgWidthCm = camWidthU * 10; // From dm to cm
  const scaleLengthCm = roundToInterval(imgWidthCm * 0.2, 10);
  const scaleLengthPx = scaleLengthCm * (imgWidthPx / imgWidthCm);
  const scaleLengthRelative = scaleLengthPx / imgWidthPx;
  const imgWidthIn = imgWidthCm * CM_TO_INCH_MULTIPLIER;
  const scaleLengthIn = roundToInterval(imgWidthIn * 0.2, 10);
  const scaleLengthPxImperial = scaleLengthIn * (imgWidthPx / imgWidthIn);
  const scaleLengthRelativeImperial = scaleLengthPxImperial / imgWidthPx;
  return {
    lengthRelative: scaleLengthRelative,
    measurementCm: scaleLengthCm,
    lengthRelativeImperial: scaleLengthRelativeImperial,
    measurementIn: scaleLengthIn,
  };
};

/**
 * Utility function to round the provided value parameter to the nearest interval of the interval parameter.
 *
 * @param {number} value the number that needs to get rounded.
 * @param {number} interval the number defining the size of the intervals that the value aims for.
 * @returns {number} value rounded to the nearest interval.
 */
const roundToInterval = (value: number, interval: number): number => Math.round(value / interval) * interval;

/**
 * Rotate a squared image, to draw it onto a canvas.
 * @param {HTMLImageElement} image The image canvas element.
 * @param {number} angle The angle in degrees.
 * @returns {HTMLCanvasElement} Rotated image as canvas element.
 */
export const rotateAndCacheImage = (image: HTMLImageElement, angle: number): HTMLCanvasElement => {
  if (image === null) return image;
  const size = [image.naturalWidth, image.naturalHeight];

  const offscreenCanvas = document.createElement('canvas'); // TODO: only use one
  const offscreenCtx = offscreenCanvas.getContext('2d');

  offscreenCanvas.width = size[0];
  offscreenCanvas.height = size[1];

  // Go to the center
  offscreenCtx!.translate(size[0] / 2, size[1] / 2);
  // Rotate (convert degrees to radians)
  offscreenCtx!.rotate((angle * Math.PI) / HALF_CIRCLE_DEGREES);
  // Move back from the center to the upper left corner
  offscreenCtx!.drawImage(image, -(offscreenCanvas.width / 2), -(offscreenCanvas.height / 2));

  /* By translating to the center, rotating and then drawing the image
     with an offset moving back, the image will be rotated by the center */

  return offscreenCanvas;
};

/**
 * Adds the compass rose with text to a given canvas.
 * @param {CanvasRenderingContext2D} ctx CanvasRenderingContext
 * @param {number} rotation Rotation in degrees.
 */
const drawCompassOnCanvas = (ctx: CanvasRenderingContext2D, rotation: number, size: number): void => {
  const imgWidth = ctx.canvas.width;

  // Some config values, relative to reference size.
  const compassSize = size;
  const compassPadding = size * IMG_COMPASS_PADDING;
  const compassSpacingToText = size * IMG_COMPASS_SPACING_TO_TEXT;
  const compassFont = size * IMG_COMPASS_FONT_SIZE;

  // Ctx.canvas.width = ctx.canvas.width + 400;
  // Get the compass from the widget, loading the png would take time (async).
  const compassImg = document.getElementsByClassName('compass-widget-img')[0];
  // Rotate it on a seperate canvas.
  const rotatedCompass = rotateAndCacheImage(compassImg as HTMLImageElement, rotation);
  // Calculate the position
  const compassX = imgWidth - (compassPadding + compassSize);
  const compassY = compassPadding;
  // Add background
  // Ctx.fillStyle = "#ffffffa0"
  // Ctx.fillRect(compass_x, compass_y, compass_size, compass_size + compass_spacing_to_text + compass_font);

  // Draw the compass rose image
  ctx.drawImage(rotatedCompass, compassX, compassY, compassSize, compassSize);

  // Write the compass rotation below the compass rose.
  const textX = compassX + compassSize * 0.5;
  const textY = compassY + compassSize + compassSpacingToText;
  ctx.fillStyle = '#000000';
  ctx.font = compassFont + IMG_COMPASS_FONT_CSS_SUFFIX;
  ctx.textAlign = 'center';
  ctx.fillText(rotation + '°', textX, textY);
};

/**
 * Download image from URI to file.
 * @param {string} uri
 * @param {string} name Name plus extension of the file.
 */
const downloadURI = (uri: string, name: string): void => {
  const link = document.createElement('a');
  link.download = name;
  link.href = uri;
  link.click();
};

// Helper function to crop a canvas by removing transparent borders.
function cropCanvas(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  let minX = canvas.width;
  let minY = canvas.height;
  let maxX = 0;
  let maxY = 0;

  // Loop through all pixels to find non-transparent bounds.
  for (let y = 0; y < canvas.height; y++) {
    for (let x = 0; x < canvas.width; x++) {
      const index = (y * canvas.width + x) * 4;
      const alpha = data[index + 3];
      if (alpha > 0) {
        // Only consider non-transparent pixels.
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

  // If no non-transparent pixels were found, return the original canvas.
  if (maxX < minX || maxY < minY) {
    return canvas;
  }

  const croppedWidth = maxX - minX + 1;
  const croppedHeight = maxY - minY + 1;

  // Create a new canvas with the cropped dimensions.
  const croppedCanvas = document.createElement('canvas');
  croppedCanvas.width = croppedWidth;
  croppedCanvas.height = croppedHeight;
  const croppedCtx = croppedCanvas.getContext('2d');
  if (croppedCtx) {
    // Draw the cropped portion from the original canvas.
    croppedCtx.drawImage(canvas, minX, minY, croppedWidth, croppedHeight, 0, 0, croppedWidth, croppedHeight);
  }

  return croppedCanvas;
}

export const exportToImageFile = async (
  scene: React.RefObject<THREE.Scene>,
  building: IBuildingProviderContext,
  resetSelection: () => void,
  data: IImageExportData,
): Promise<File> => {
  const { showGeneralInfo, width, height, pxPerDm, format, transparent, backgroundColor, unitType } = data;

  // Prevent error when empty
  if (width < 1 || height < 1 || building.walls.length === 0) {
    throw new Error('Invalid dimensions or empty building');
  }

  // Determine if compass/general info should be drawn.
  const showGeneralInfoSafe = showGeneralInfo && !!document.getElementsByClassName('compass-widget-img')[0];

  // Get bounding box and calculate aspect ratio.
  const bounds = getBoundingBox(building);
  const ratio = bounds.width / (bounds.height === 0 ? PREVENT_DIVISION_BY_ZERO : bounds.height);

  // --- CALCULATE IMAGE SIZE ---
  let imageWidth = width;
  let imageHeight = height;

  if (isNaN(imageWidth) && isNaN(imageHeight)) {
    imageWidth = bounds.width * pxPerDm;
    imageHeight = bounds.height * pxPerDm;
    const maxSize = DEFAULT_AUTO_REFERENCE_SLICE * DEFAULT_CANVAS_MAX_SIZE_MULTIPLIER;
    if (imageWidth >= imageHeight) {
      if (imageWidth >= maxSize) {
        imageWidth = maxSize; // Clamp
        imageHeight = NaN; // Set to auto (recalculate if needed)
      }
    } else if (imageHeight >= maxSize) {
      imageHeight = maxSize; // Clamp
      imageWidth = NaN; // Set to auto (recalculate if needed)
    }
  }

  if (isNaN(imageHeight)) {
    imageHeight = imageWidth / ratio;
  }
  if (isNaN(imageWidth)) {
    imageWidth = imageHeight * ratio;
  }

  if (imageWidth < 1 || imageHeight < 1 || isNaN(imageWidth) || isNaN(imageHeight)) {
    throw new Error('Calculated invalid image dimensions');
  }

  // --- APPLY SAFE CLAMPING (Wrapper) ---
  const safeDims = getSafeDimensions(imageWidth, imageHeight);
  // We don't overwrite imageWidth directly yet, but we will use the safe dimensions to check wrapper size

  // Wrapper logic
  let compassSize = (safeDims.width < safeDims.height ? safeDims.width : safeDims.height) * IMG_COMPASS_SIZE;
  const extraW = showGeneralInfoSafe ? compassSize * 2 : 0;

  // Check if wrapper exceeds limits (Wrapper Clamp)
  const safeWrapper = getSafeDimensions(safeDims.width + extraW, safeDims.height);

  // If wrapper needs to shrink, shrink everything proportionally
  if (safeWrapper.width < safeDims.width + extraW) {
    const s = safeWrapper.width / (safeDims.width + extraW);
    imageWidth = Math.floor(safeDims.width * s);
    imageHeight = Math.floor(safeDims.height * s);
    // Recalculate compass size with new smaller dimensions
    compassSize = (imageWidth < imageHeight ? imageWidth : imageHeight) * IMG_COMPASS_SIZE;
  } else {
    // Otherwise just use the standard clamped values
    imageWidth = safeDims.width;
    imageHeight = safeDims.height;
  }
  // --- END CLAMPING ---

  // Create offscreen canvas.
  const offscreenCanvas = document.createElement('canvas');
  const ctx = offscreenCanvas.getContext('2d');
  // Expand canvas width if compass is drawn.
  offscreenCanvas.width = imageWidth + (showGeneralInfoSafe ? compassSize * 2 : 0);
  offscreenCanvas.height = imageHeight;

  // Draw background if not transparent.
  if (transparent === false) {
    ctx!.fillStyle = backgroundColor;
    ctx!.fillRect(0, 0, offscreenCanvas.width, offscreenCanvas.height);
  }

  // Render scene image.
  const { imgCanvas, scaleLengthRelative, scaleMeasurementCm, scaleLengthRelativeImperial, scaleMeasurementIn } = await createSceneImage(
    scene,
    bounds,
    resetSelection,
    imageWidth,
    imageHeight,
    1,
    format,
    true,
    showGeneralInfoSafe,
  );

  // Draw the rendered scene onto the offscreen canvas.
  ctx!.drawImage(imgCanvas, showGeneralInfoSafe ? compassSize : 0, 0);

  // Draw compass and scale if applicable.
  if (showGeneralInfoSafe) {
    drawScaleOnCanvas(ctx!, unitType, scaleLengthRelative * imageWidth, scaleMeasurementCm, scaleLengthRelativeImperial * imageWidth, scaleMeasurementIn);
  }

  // For PNG with transparency, crop to remove empty space; otherwise, use the original canvas.
  let exportCanvas = offscreenCanvas;
  if (format === 'png' && transparent) {
    exportCanvas = cropCanvas(offscreenCanvas);
  }

  // For PNG, ensure that the maximum width is 2560px while preserving aspect ratio.
  if (exportCanvas.width > 2560) {
    const scaleFactor = 2560 / exportCanvas.width;
    const newWidth = 2560;
    const newHeight = exportCanvas.height * scaleFactor;
    const scaledCanvas = document.createElement('canvas');
    scaledCanvas.width = newWidth;
    scaledCanvas.height = newHeight;
    const scaledCtx = scaledCanvas.getContext('2d');
    scaledCtx!.drawImage(exportCanvas, 0, 0, newWidth, newHeight);
    exportCanvas = scaledCanvas;
  }

  // Convert the canvas to a Blob and wrap it in a File.
  return new Promise((resolve, reject) => {
    const handleBlob = (blob: Blob | null, mimeType: string) => {
      if (blob) {
        const file = new File([blob], `plan.${format}`, { type: mimeType });
        resolve(file);
      } else {
        reject(new Error('Canvas toBlob conversion failed'));
      }
    };

    if (format === 'jpg' || format === 'jpeg') {
      exportCanvas.toBlob((blob) => handleBlob(blob, 'image/jpeg'), 'image/jpeg', 0.6);
    } else if (format === 'png') {
      exportCanvas.toBlob((blob) => handleBlob(blob, 'image/png'), 'image/png');
    } else {
      exportCanvas.toBlob((blob) => handleBlob(blob, 'image/' + format), 'image/' + format);
    }
  });
};

/**
 * Downloads the building to file.
 * @param {RefObject<Scene>} scene Reference to the scene.
 * @param {IBuildingProviderContext} building Reference to the building.
 * @param {Function} resetSelection Function that resets all selection.
 * @param {number} compassRotation Compass rotation in degrees.
 * @param {IImageExportData} data object containing other relevant values.
 * @returns {boolean} true on success, false on failure
 */
// eslint-disable-next-line complexity
export const exportToImage = async (
  scene: RefObject<THREE.Scene>,
  building: IBuildingProviderContext,
  resetSelection: () => void,
  compassRotation: number,
  data: IImageExportData,
): Promise<boolean> => {
  const { showGeneralInfo, width, height, pxPerDm, fileName, format, transparent, backgroundColor, unitType } = data;

  // Prevent error when empty
  if (width < 1 || height < 1 || building.walls.length === 0) return false;

  // Prevent error when trying to export with general info, but general info is disabled
  const showGeneralInfoSafe = showGeneralInfo && !!document.getElementsByClassName('compass-widget-img')[0];

  // Get bounding box of the building.
  const bounds = getBoundingBox(building);
  // Aspect ratio of the buildings bounds.
  const ratio = bounds.width / (bounds.height === 0 ? PREVENT_DIVISION_BY_ZERO : bounds.height);

  // --- CALCULATE IMAGE SIZE ---
  // Create a copy of the input sizes for the actual image size.
  let imageWidth = width;
  let imageHeight = height;

  // In case of both dimensions are set to auto:
  if (isNaN(imageWidth) && isNaN(imageHeight)) {
    // If both dimensions are on auto, use the auto reference size for the bigger dimension.
    imageWidth = bounds.width * pxPerDm;
    imageHeight = bounds.height * pxPerDm;
    // Maximum canvas size - if too big, only white image will be rendered
    const maxSize = DEFAULT_AUTO_REFERENCE_SLICE * DEFAULT_CANVAS_MAX_SIZE_MULTIPLIER;
    // Get the bigger dimension size, clamp that to max size and then re-adjust the other dimension
    if (imageWidth >= imageHeight) {
      if (imageWidth >= maxSize) {
        imageWidth = maxSize; //Clamp
        imageHeight = NaN; // Set to auto (recalculate if other dim gets clamped)
      }
    } else if (imageHeight >= maxSize) {
      imageHeight = maxSize; //Clamp
      imageWidth = NaN; // Set to auto (recalculate if other dim gets clamped)
    }
  }

  // Fill in the missing dimension, by calculating it from the building's ratio
  if (isNaN(imageHeight)) {
    imageHeight = imageWidth / ratio;
  }
  if (isNaN(imageWidth)) {
    imageWidth = imageHeight * ratio;
  }

  // --- END OF CALCULATE IMAGE SIZE ---
  // Return condition for error
  if (imageWidth < 1 || imageHeight < 1 || isNaN(imageWidth) || isNaN(imageHeight)) return false;

  // --- APPLY SAFE CLAMPING (Wrapper) ---
  const safeDims = getSafeDimensions(imageWidth, imageHeight);

  let compassSize = (safeDims.width < safeDims.height ? safeDims.width : safeDims.height) * IMG_COMPASS_SIZE;
  const extraW = showGeneralInfoSafe ? compassSize * 2 : 0;

  const safeWrapper = getSafeDimensions(safeDims.width + extraW, safeDims.height);

  if (safeWrapper.width < safeDims.width + extraW) {
    const s = safeWrapper.width / (safeDims.width + extraW);
    imageWidth = Math.floor(safeDims.width * s);
    imageHeight = Math.floor(safeDims.height * s);
    compassSize = (imageWidth < imageHeight ? imageWidth : imageHeight) * IMG_COMPASS_SIZE;
  } else {
    imageWidth = safeDims.width;
    imageHeight = safeDims.height;
  }
  // --- END CLAMPING ---

  // Create a new canvas, for the result image.
  const offscreenCanvas = document.createElement('canvas');
  const ctx = offscreenCanvas.getContext('2d');
  // Set width and height of the canvas
  // If the compass is drawn, expand to left and right
  offscreenCanvas.width = imageWidth + (showGeneralInfoSafe ? compassSize * 2 : 0);
  offscreenCanvas.height = imageHeight;

  // Draw a background (if not transparent).
  if (transparent === false) {
    ctx!.fillStyle = backgroundColor;
    ctx!.fillRect(0, 0, offscreenCanvas.width, offscreenCanvas.height);
  }

  // Get a clean image of the building, forced into the specified size. (transparent)
  const { imgCanvas, scaleLengthRelative, scaleMeasurementCm, scaleLengthRelativeImperial, scaleMeasurementIn } = await createSceneImage(
    scene,
    bounds,
    resetSelection,
    imageWidth,
    imageHeight,
    1,
    format,
    true,
    showGeneralInfoSafe,
  );
  // Add the scene image to the canvas.
  ctx!.drawImage(imgCanvas, showGeneralInfoSafe ? compassSize : 0, 0);

  // Add the compass to the canvas.
  if (showGeneralInfoSafe) {
    drawCompassOnCanvas(ctx!, compassRotation, compassSize);
    // If compass is added, also move the scale to the right, since the canvas gets expanded
    bounds.height * pxPerDm;
    drawScaleOnCanvas(ctx!, unitType, scaleLengthRelative * imageWidth, scaleMeasurementCm, scaleLengthRelativeImperial * imageWidth, scaleMeasurementIn);
  }

  // Download the final image.
  // Use the quality parameter for jpg compression (70% quality)
  const finalImage = format === 'jpg' || format === 'jpeg' ? offscreenCanvas.toDataURL('image/jpeg', 0.6) : offscreenCanvas.toDataURL('image/' + format);

  downloadURI(finalImage, fileName + '.' + format);

  return true; // Return true for success.
};

/**
 * Function to draw the line and text that make up the scale in the lower right corner, to better understand the dimensions in the rendered floor plan.
 *
 * @param {CanvasRenderingContext2D} ctx the canvas rendering context object.
 * @param {number} scaleLengthPx the length of the scale line in pixels for metric.
 * @param {number} scaleMeasurementCm the length of the scale line in centimeters.
 * @param {number} scaleLengthPxImperial the length of the scale line in pixels for imperial.
 * @param {number} scaleMeasurementIn the length of the scale line in inches.
 */
const drawScaleOnCanvas = (
  ctx: CanvasRenderingContext2D,
  unitType: EUnitType,
  scaleLengthPx: number,
  scaleMeasurementCm: number,
  scaleLengthPxImperial: number,
  scaleMeasurementIn: number,
): void => {
  const imgWidthPx = ctx.canvas.width; // Image width in pixels, used to calculate layout and sizes
  const imgHeightPx = ctx.canvas.height; // Image height in pixels, used to calculate layout and sizes

  if (unitType === EUnitType.METRIC) {
    /**
     * Problem: We need to draw the scale's text and thickness at an approximately same size for every image size.
     * Solution: Setup reference sizes, relative to a fixed image size. Then calculate a size factor which can
     * scale the reference sizes to the actual image size in dependency of the fixed image reference sizes.
     */
    const sizeFactor = (imgWidthPx / IMG_SCALE_REF_SIZE[0] + imgHeightPx / IMG_SCALE_REF_SIZE[1]) / 2;

    // Calculate some absolute values, from the reference sizes and sizeFactor.
    const paddingRight = IMG_SCALE_REF_PADDING_RIGHT * sizeFactor;
    const paddingBottom = IMG_SCALE_REF_PADDING_BOTTOM * sizeFactor;
    const lineThickness = IMG_SCALE_REF_LINE_THICKNESS * sizeFactor;
    const fontSize = Math.round(sizeFactor * IMG_SCALE_REF_FONTSIZE);
    const scaleText = scaleMeasurementCm.toString() + 'cm';
    const textGap = fontSize;

    // Set some settings for drawing the label text.
    ctx.font = fontSize + 'px ' + IMG_SCALE_FONT;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    // Get the width of the label, to place the line next to it.
    const labelWidth = ctx.measureText(scaleText).width + textGap;
    // The color of the line and the label.
    ctx.fillStyle = IMG_SCALE_COLOR;

    // Calculate the middle Y position of the line and the label.
    const scaleMiddleYPos = imgHeightPx - fontSize * 0.5 - paddingBottom;

    // Draw the scale's line (as a rect).
    ctx.fillRect(imgWidthPx - scaleLengthPx - paddingRight - labelWidth, scaleMiddleYPos - lineThickness * 0.5, scaleLengthPx, lineThickness);

    // Draw the scale's label.
    ctx.fillText(scaleText, imgWidthPx - paddingRight - labelWidth * 0.5, scaleMiddleYPos);
  } else {
    /**
     * Problem: We need to draw the scale's text and thickness at an approximately same size for every image size.
     * Solution: Setup reference sizes, relative to a fixed image size. Then calculate a size factor which can
     * scale the reference sizes to the actual image size in dependency of the fixed image reference sizes.
     */
    const sizeFactor = (imgWidthPx / IMG_SCALE_REF_SIZE[0] + imgHeightPx / IMG_SCALE_REF_SIZE[1]) / 2;

    // Calculate some absolute values, from the reference sizes and sizeFactor.
    const paddingRight = IMG_SCALE_REF_PADDING_RIGHT * sizeFactor;
    const paddingBottom = IMG_SCALE_REF_PADDING_BOTTOM * sizeFactor;
    const lineThickness = IMG_SCALE_REF_LINE_THICKNESS * sizeFactor;
    const fontSize = Math.round(sizeFactor * IMG_SCALE_REF_FONTSIZE);
    const scaleText = scaleMeasurementIn.toString() + 'in';
    const textGap = fontSize;

    // Set some settings for drawing the label text.
    ctx.font = fontSize + 'px ' + IMG_SCALE_FONT;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    // Get the width of the label, to place the line next to it.
    const labelWidth = ctx.measureText(scaleText).width + textGap;
    // The color of the line and the label.
    ctx.fillStyle = IMG_SCALE_COLOR;

    // Calculate the middle Y position of the line and the label.
    const scaleMiddleYPos = imgHeightPx - fontSize * 0.5 - paddingBottom;

    // Draw the scale's line (as a rect).
    ctx.fillRect(imgWidthPx - scaleLengthPxImperial - paddingRight - labelWidth, scaleMiddleYPos - lineThickness * 0.5, scaleLengthPxImperial, lineThickness);

    // Draw the scale's label.
    ctx.fillText(scaleText, imgWidthPx - paddingRight - labelWidth * 0.5, scaleMiddleYPos);
  }
};

/**
 * Draws a horizontal line at the cursor position, centered horizontally on the page.
 *
 * @param {PdfCursor} cursor the object that takes care of the current position and page count.
 * @param {jsPDF} pdf the object handling the actual export to pdf.
 * @param {number} width the width/thickness in which the line should get rendered.
 * @param {number} offsetY possible offset on the y-axis.
 */
const addHorizontalDividerLine = (cursor: PdfCursor, pdf: jsPDF, width: number, offsetY: number = 0): void => {
  pdf.setLineWidth(HORIZONTAL_DIVIDER_LINE_THICKNESS);
  const rmbX = cursor.x; // Remember the current x position, to later restore it
  cursor.x = (cursor.pageSize.width - width) * 0.5; // Left starting point of the line
  pdf.line(cursor.x, cursor.y + offsetY, cursor.x + width, cursor.y + offsetY);
  cursor.x = rmbX; // Restore cursor x position before this function
};

/**
 * Draws the header onto the current page.
 *
 * @param {PdfCursor} cursor the pdf info object that will remember the page number and position on it.
 * @param {jsPDF} pdf the jsPDF object that will export the data for us.
 * @param {IFileInfo} fileInfo the fileInfo object containing values needed for the exporting file.
 * @param {IntlShape} intl - The `react-intl` instance.
 */
const drawHeader = (cursor: PdfCursor, pdf: jsPDF, fileInfo: IFileInfo, intl: IntlShape): void => {
  const headerWidth = cursor.innerPageSize.width - HEADER_PADDING_X * 2;
  const rmbFontSize = pdf.getFontSize();

  pdf.setFontSize(HEADER_FONT_SIZE);
  cursor.addHeight(HEADER_SPACING_TOP);
  cursor.addWidth(HEADER_PADDING_X);
  // Write the project title on the left side
  pdf.text(fileInfo.projectTitle, cursor.x, cursor.y, {
    baseline: 'top',
    align: 'left',
  });

  cursor.addWidth(headerWidth); // Move the cursor all the way to the right side of the header
  // Format the date to string
  const dateFormatted = formatDateIntl(fileInfo.creationDate, intl);
  // Write the date on the right side
  pdf.setFontSize(HEADER_FONT_SIZE / 1.3);
  pdf.text(dateFormatted, cursor.x, cursor.y, {
    baseline: 'top',
    align: 'right',
  });
  // Format the date to a time stamp.
  const timeFormatted = formatTime(fileInfo.creationDate);
  pdf.setFontSize(pdf.getFontSize() / 1.12);
  cursor.addHeight(pdf.getFontSize() / 2.25);
  // Write the date on the right side
  pdf.text(timeFormatted, cursor.x, cursor.y, {
    baseline: 'top',
    align: 'right',
  });

  // Move cursor below the text
  const textDimensions = pdf.getTextDimensions('XXX'); // Get the approximate height of the fontSize of the title
  cursor.addHeight(textDimensions.h + HEADER_FONT_SPACING_BOTTOM);
  // Add a horizontal line
  addHorizontalDividerLine(cursor, pdf, headerWidth);
  cursor.addHeight(HEADER_SPACING_BOTTOM);

  pdf.setFontSize(rmbFontSize);
};

/**
 * Ouputs the PDF.
 *
 * @param {jsPDF} pdf Object reference of the jsPDF class.
 * @param {boolean} direct flag to indicate if the pdf should get saved directly or incrementally.
 * @param {string} fileName filename without the extension.
 */
const outputPDF = (pdf: jsPDF, direct: boolean, fileName: string): void => {
  const fullname = fileName + '.pdf';
  if (direct) {
    pdf.save(fullname);
  } else {
    /*
      Const data = pdf.output("dataurlnewwindow", { filename: fullname });
      const fileURL = window.URL.createObjectURL(data);
      const tab = window.open();
      tab.document.title = fullname;
      tab.location.href = fileURL;
    */

    pdf.output('dataurlnewwindow', {
      filename: fullname,
    });
  }
};

/**
 * Draws the header and possible other values onto the current page.
 *
 * @param {PdfCursor} cursor the PdfCursor object controlling page number and current position.
 * @param {jsPDF} pdf the jsPDF object handling the drawing to the canvas and exporting of pdfs.
 * @param {IFileInfo} fileInfo the file info object that contains additional information related to the soon to be exported file.
 * @param {IntlShape} intl - The `react-intl` instance.
 */
const decorateNewPage = (cursor: PdfCursor, pdf: jsPDF, fileInfo: IFileInfo, intl: IntlShape) => {
  drawHeader(cursor, pdf, fileInfo, intl);
};

/**
 * Adds the rotated compass image + rotation as text below.
 *
 * @param {jsPDF} pdf reference to the jsPDF object.
 * @param {number} rotation in degrees.
 * @param {[number, number]} pos upper right corner of the scene image.
 */
const addCompass = (pdf: jsPDF, rotation: number, pos: [number, number]): void => {
  // Settings

  // Calc position of the image
  const posX = pos[0] - COMPASS_PADDING.x - COMPASS_SIZE;
  const posY = pos[1] + COMPASS_PADDING.y;

  // Draw a background rect, in case the scene content overlaps with the compass
  pdf.saveGraphicsState();
  // Modify the graphics state, to set an opacity - later restore it to the saved graphics state
  const newGState = pdf.GState({ opacity: COMPASS_BACKGROUND_OPACITY });
  pdf.setGState(newGState);
  pdf.setFillColor(COMPASS_BACKGROUND_COLOR);
  // Draw a rect around the compass image and text, that get drawn later
  // Pdf.rect(
  //     PosX,
  //     PosY,
  //     GetCompassHeight().width,
  //     GetCompassHeight(),
  //     Draw_container_stroke ? "FD" : "F"
  // )
  pdf.restoreGraphicsState();

  // Get the compass image from the compass widget (loading the image seperately here would require an async process of loading it)
  const img = document.getElementsByClassName('compass-widget-img')[0];
  // Draw the image rotated onto an offscreen canvas
  const rotated = rotateAndCacheImage(img as HTMLImageElement, rotation);

  pdf.addImage({
    imageData: rotated.toDataURL('image/png'),
    format: 'PNG',
    x: posX,
    y: posY,
    width: COMPASS_SIZE,
    height: COMPASS_SIZE,
    // Not using this rotation, since it does not rotate around the center of the image
    rotation: 0,
  });

  // Remember old font size, then set new font size
  const rmbFontSize = pdf.getFontSize();
  pdf.setFontSize(COMPASS_FONT_SIZE);
  // Add the rotation as text below the compass image
  pdf.text(rotation.toString() + '°', posX - COMPASS_SPACING_TO_TEXT, posY + COMPASS_SIZE / 2, {
    align: 'right',
    baseline: 'middle',
  });
  // Restore old font size
  pdf.setFontSize(rmbFontSize);
};

/**
 * Function to export a plan to pdf.
 *
 * @param {exportToPdfProps} props the props object containing all params below.
 * - param {Scene} scene the scene reference object of our floor planner.
 * - param {IBuilding} building IBuilding object representing the complete building.
 * - param {() => void} resetSelection function to reset all previously made selections.
 * - param {IPdfSettings} settings IPdfSettings object containing all relevant settings.
 * - param {IExportData} data IExportData object containing all relevant data for the export.
 * - param {jsPDF} jsPdf the jsPDF object containing the functions for exporting to pdf.
 *  @param {IntlShape} intl - The `react-intl` instance.
 * @returns {Promise<boolean>} flag to indicate if the export action was successful.
 */
export const exportToPdf = async (props: exportToPdfProps, intl: IntlShape): Promise<boolean> => {
  const {
    scene,
    building,
    resetSelection,
    settings,
    data: {
      // Data
      projectTitle = '',
      fileName = '',
      compassRotation = 0,
      text = [],
    },
    unitType,
  } = props;
  // Prevent error when trying to export with general info, but general info is disabled
  const showGeneralInfoSafe = settings.showGeneralInfo !== undefined ? settings.showGeneralInfo : true && !!document.getElementsByClassName('compass-widget-img')[0];

  const bounds = getBoundingBox(building);
  if (bounds.height === 0 || bounds.width === 0) return false;

  // Parse the EPageOrientationInputParameter into the EPageOrientation enum.
  // Selecting the correct orientation for the "auto" option.
  const orientation = parseOrientation(settings.orientation !== undefined ? settings.orientation : EPageOrientationInputParameter.AUTO, bounds);
  // Setting up the relevant objects for exporting the floor planner scene to a pdf.
  // Documentation: https://raw.githack.com/MrRio/jsPDF/master/docs/index.html
  const jsPdf = new jsPDF({
    orientation: orientation,
    unit: 'mm',
  });

  // Calculate the aspect ratio of the building (prevent division by zero)
  const ratio = bounds.width / (bounds.height === 0 ? PREVENT_DIVISION_BY_ZERO : bounds.height);

  // Create a cursor which saves the current position on page and page number.
  const cursor = new PdfCursor(jsPdf, settings.orientation === EPageOrientationInputParameter.PORTRAIT ? true : false);

  // Creating the typed file information object.
  const fileInfo: IFileInfo = {
    fileName: fileName,
    projectTitle: projectTitle,
    creationDate: new Date(),
  };

  jsPdf.setProperties({ title: fileInfo.fileName }); // This is important for the blob shown if opened in browser
  // Add the roboto font to the vFS (virtual File System) - https://codesandbox.io/p/sandbox/jspdf-add-font-bm4xy?file=%2Fsrc%2Findex.js
  jsPdf.addFileToVFS('Roboto-Regular-normal.ttf', FontRoboto);
  jsPdf.addFont('Roboto-Regular-normal.ttf', 'Roboto', 'normal');
  jsPdf.setFont('Roboto');
  const headerWidth = cursor.innerPageSize.width - HEADER_PADDING_X * 2;

  // --- ADD THE PROJECT TITLE ---
  jsPdf.setFontSize(TITLE_FONT_SIZE);
  cursor.addWidth(headerWidth * 0.055);
  jsPdf.text(fileInfo.projectTitle, cursor.x, cursor.y, {
    baseline: 'top',
    align: 'left',
  });

  // --- ADD THE DATE ON FIRST PAGE ---
  /*
   * Move the cursor all the way to the right side of the header.
   * Needs to get pushed further than the calculated header width, because we decrease the font size.
   */
  cursor.addWidth(headerWidth * 1.055);
  if (settings.showDate) {
    jsPdf.setFontSize(HEADER_FONT_SIZE / 1.1);
    const dateFormatted = formatDateIntl(fileInfo.creationDate, intl);
    jsPdf.text(dateFormatted, cursor.x, cursor.y, {
      baseline: 'top',
      align: 'right',
    });
    // Format the date to a time stamp.
    const timeFormatted = formatTime(fileInfo.creationDate);
    jsPdf.setFontSize(jsPdf.getFontSize() / 1.12);
    cursor.addHeight(jsPdf.getFontSize() / 2.25);
    // Write the time below.
    jsPdf.text(timeFormatted, cursor.x, cursor.y, {
      baseline: 'top',
      align: 'right',
    });
    cursor.addHeight(TEXT_FONT_SIZE * FONT_TO_MM + TITLE_SPACING_BOTTOM);
  }
  cursor.addWidth(-(headerWidth * 1.055)); // Resetting the position of the pdfCursor.

  const compassY = cursor.y;
  if (showGeneralInfoSafe) {
    cursor.addHeight(COMPASS_SIZE * PDF_CURSOR_HEIGHT_OFFSET_MULTIPLIER);
  }

  // --- ADD THE SCENE IMAGE ---
  const imgWidth = cursor.innerPageSize.width;
  const maxHeight = cursor.getRemainingPageHeight(0);
  let imgHeight = 0;

  if (orientation.toString() === EPageOrientationInputParameter.LANDSCAPE) {
    imgHeight = maxHeight;
  } else {
    // Orientation === Portrait
    if (settings.fixedSquare) {
      imgHeight = cursor.innerPageSize.width;
    } else {
      // Dynamic space for the scene image
      imgHeight = imgWidth / ratio;
      // If the image height almost takes up the remaining page height, use the whole remaining page height
      if (imgHeight > maxHeight * USE_FULL_SIZE_PERCENTAGE) imgHeight = maxHeight;
    }
  }

  // Clamp the image height to the remaining page height so that the image does not exceed the page
  if (imgHeight > maxHeight) imgHeight = maxHeight;

  // INQUIRE: Is this still needed?
  cursor.setImageDimensions({ width: imgWidth, height: imgHeight });

  // Create and add an image of the building in the scene, forced into the given size.
  const { imgCanvas, scaleLengthRelative, scaleMeasurementCm, scaleLengthRelativeImperial, scaleMeasurementIn } = await createSceneImage(
    scene,
    bounds,
    resetSelection,
    imgWidth,
    imgHeight,
    settings.imgUpres as number,
    'jpeg',
    false,
    showGeneralInfoSafe,
  );
  jsPdf.addImage(imgCanvas, 'JPEG', cursor.x, cursor.y, imgWidth, imgHeight);

  cursor.addWidth(-MASTER_PAGE_PADDING / 1.4);
  jsPdf.addImage(imgCanvas, 'JPEG', cursor.x, cursor.y, imgWidth, imgHeight);

  // Border, mostly for debug
  if (DRAW_IMAGE_BORDER) {
    jsPdf.rect(cursor.x, cursor.y, imgWidth, imgHeight, 'S');
  }

  if (showGeneralInfoSafe) {
    addCompass(jsPdf, compassRotation, [cursor.getMaxX(), compassY]);
  }

  // Increment cursor after image.
  cursor.addHeight(imgHeight);

  // --- DRAW THE SCALE ---

  // Go a little bit up again, so the scale is slightly inside the image.
  cursor.addHeight(-PDF_SCALE_UP_TRANSLATE);

  jsPdf.setLineWidth(PDF_SCALE_LINE_THICKNESS);
  jsPdf.setFontSize(PDF_SCALE_LABEL_FONTSIZE);
  // TODO: Replace hard-coded unit types with localized variants and for imperial units as well.
  const scaleLabelText = unitType === EUnitType.METRIC ? scaleMeasurementCm.toString() + 'cm' : scaleMeasurementIn.toString() + 'in';

  jsPdf.text(scaleLabelText, cursor.getMaxX() - PDF_SCALE_PADDING_RIGHT, cursor.y, {
    /* Text options */
    align: 'right',
    baseline: 'middle',
  });
  const scaleLabelWidth = jsPdf.getTextWidth(scaleLabelText);
  const scaleLineLength = unitType === EUnitType.METRIC ? scaleLengthRelative * imgWidth : scaleLengthRelativeImperial * imgWidth;

  const scaleLineStartPoint = cursor.getMaxX() - scaleLabelWidth - scaleLineLength - PDF_SCALE_PADDING_RIGHT - PDF_SCALE_LABEL_GAP;
  jsPdf.line(scaleLineStartPoint, cursor.y, scaleLineStartPoint + scaleLineLength, cursor.y);

  cursor.addHeight(IMAGE_SPACING_BOTTOM);

  // --- ADD TEXT ---
  jsPdf.setFontSize(TEXT_FONT_SIZE);

  const maxTextWidth = cursor.getRemainingPageWidth(0) - TEXT_PADDING_X * 2;
  let _drawImageDivider = true;
  // Go through each block of the text (each block is one element in array).
  for (let i = 0; i < text.length; i++) {
    // Get the size of the textblock.
    const textDimensions = jsPdf.getTextDimensions(text[i], {
      maxWidth: maxTextWidth,
      fontSize: TEXT_FONT_SIZE,
    });
    // If there is NOT enough y space for the textblock:
    if (cursor.isBelowPage(textDimensions.h)) {
      _drawImageDivider = false; // Dont draw a divider below the image if there is no text on first page.
      jsPdf.addPage('a4', orientation); // Add a new page.
      // Increment the cursor by one page - and tell it to draw header.
      cursor.incrementPage(async () => decorateNewPage(cursor, jsPdf, fileInfo, intl));
      // (TODO: if whole textblock is bigger than one page, break it)
    }

    // Add text, then adjust cursor.
    cursor.x = cursor.pagePadding + TEXT_PADDING_X;
    jsPdf.text(text[i], cursor.x, cursor.y, {
      maxWidth: maxTextWidth,
      baseline: 'top',
    });
    cursor.addHeight(textDimensions.h + TEXT_BLOCK_PADDING_Y);
  }

  // Do this at the very end - add a footer to every page.
  drawFooterOnAllPages(jsPdf, cursor);

  outputPDF(jsPdf, settings.directDownload !== undefined ? settings.directDownload : true, fileInfo.fileName);

  return true;
};

/**
 * Utility function to draw the footer onto the pages of the pdf export.
 *
 * @param {jsPDF} pdf the jsPDF object that is currently preparing the export data.
 * @param {PdfExportInfo} cursor the cursor object that is used to keep track of page numbers and the current render position.
 */
const drawFooterOnAllPages = (pdf: jsPDF, cursor: PdfCursor): void => {
  // Remember the font size before the function
  const rmbFontSize = pdf.getFontSize();
  // Get the total number of pages in the pdf.
  const pages = pdf.getNumberOfPages();
  // Go through each page and draw the footer.
  for (let i = 0; i < pages; i++) {
    pdf.setPage(i + 1); // JsPDF starts count at 1 :(
    // Move cursor to the bottom center, set font size, draw the text.
    cursor.setBottom();
    cursor.centerX();
    pdf.setFontSize(PAGE_NUMBER_FONT_SIZE);
    pdf.text(i + 1 + '/' + pages, cursor.x, cursor.y, {
      align: 'center',
      baseline: 'bottom',
    });
  }
  // Restore previous values (not needed since this is the last operation in the pdf, but just to be safe).
  pdf.setFontSize(rmbFontSize);
  cursor.resetPos();
};

/**
 * Parses the EPageOrientationInputParameter to the EPageOrientation enum.
 * These 2 enums are needed because:
 * 1. The Input enum contains an "auto" option, while the EPageOrientation enum only contains "portrait" and "landscape".
 * 2. The jsPDF-library only accepts "portrait" or "landscape" (no "auto").
 * So this function decides whether to use "portrait" or "landscape" for the auto orientation,
 * depending on the aspect of the exported image of the building.
 *
 * @param {EPageOrientationInputParameter} inputOrientation the input orientation for exporting the page.
 * @param {IBoundingBox} bounds the bounding box of the image of the building.
 * @returns {EPageOrientation} the orientation correctly parsed for jsPDF.
 */
const parseOrientation = (inputOrientation: EPageOrientationInputParameter, bounds: IBoundingBox): EPageOrientation => {
  switch (inputOrientation) {
    case EPageOrientationInputParameter.PORTRAIT:
      return EPageOrientation.PORTRAIT;
    case EPageOrientationInputParameter.LANDSCAPE:
      return EPageOrientation.LANDSCAPE;
    // In case of EPageOrientationInputParameter.auto:
    default:
      return bounds.width > bounds.height ? EPageOrientation.LANDSCAPE : EPageOrientation.PORTRAIT;
  }
};

/**
 * Defines the required options for the IFC export function.
 */
interface IExportIFCOptions {
  planJsonString: string;
  token: string;
  fileName: string;
}

/**
 * Exports the current plan to an IFC file by calling the IFC API.
 * @param options - The required data for the export.
 * @returns A promise that resolves if the download is successful and rejects if it fails.
 */
export const exportToIFC = async (options: IExportIFCOptions): Promise<void> => {
  const { planJsonString, token, fileName } = options;
  const activeToken = await getValidKeycloakAccessTokenOrFallback(token);

  if (!activeToken) {
    throw new Error('No valid authentication token available.');
  }

  const response = await fetch(IFC_URL, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${activeToken}`,
      'Content-Type': 'application/json',
    },
    body: planJsonString,
  });

  if (!response.ok) {
    // Throws an error that will be caught by the calling function
    throw new Error(`Failed to download IFC file. Status: ${response.status}`);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileName}.ifc`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
